'use client';

import { useCallback, useState, type ReactNode } from 'react';
import { Check, Copy, ExternalLink } from 'lucide-react';
import type { CustomAdapter } from '../../data/mock-adapters';
import { cn, truncateAddress } from '@/lib/utils';
import { useExternalLinkGuard } from '../../ui/ExternalLinkGuard';
import { AdapterPanelShell } from './AdapterPanelShell';

/** Build a block-explorer "address" URL on the chain configured via
 *  `NEXT_PUBLIC_CHAIN_ID`. Mirrors the host-resolution used elsewhere
 *  in the lobby (e.g. `grid-to-adapters.ts#basescanLink`) so the
 *  Custom adapter Contract row opens on the same explorer the chip
 *  row already does. Returns `null` when `address` doesn't look like a
 *  hex address — keeps the click behaviour out of the DOM rather than
 *  pointing at a 404. */
function blockExplorerAddressUrl(address: string | undefined): string | null {
  if (!address || !/^0x[0-9a-fA-F]{40}$/.test(address)) return null;
  const host =
    process.env.NEXT_PUBLIC_CHAIN_ID === '8453'
      ? 'https://basescan.org'
      : 'https://sepolia.basescan.org';
  return `${host}/address/${address}`;
}

/**
 * Custom adapter panel.
 *
 * Boards can register arbitrary adapters that don't fit the standard
 * platforms — game worlds, gated NFTs, bespoke vaults. We also fall back
 * to this panel when the indexer hasn't yet enriched a Flaunch / LP asset
 * (e.g. a freshly-tracked v2.1 adapter whose `AssetDeposited` event is
 * still in-flight), so showing fabricated "$0 / 0% / 0" stats reads as
 * misleading. The panel deliberately drops the APR / TVL / Accrued grid
 * and only surfaces the contract identity + creator-authored copy
 * (title, description, image, links) handled by `AdapterPanelShell`.
 *
 * Contract row layout: `[CONTRACT] 0x1234…abcd ↗  [📋]`
 *   - Address truncated and linked to Basescan via `useExternalLinkGuard`
 *     (DYOR interstitial fires for non-trusted hosts).
 *   - Standalone copy button next to the link so clipboard access
 *     doesn't double-fire the explorer navigation.
 */
export function CustomAdapterPanel({
  adapter,
  belowStats,
}: {
  adapter: CustomAdapter;
  belowStats?: ReactNode;
}) {
  const contractHref = blockExplorerAddressUrl(adapter.contractAddress);
  const { guardClick } = useExternalLinkGuard();
  const [copied, setCopied] = useState(false);

  const onCopy = useCallback(
    async (e: React.MouseEvent | React.KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!adapter.contractAddress) return;
      try {
        await navigator.clipboard.writeText(adapter.contractAddress);
        setCopied(true);
        // Keep the check icon visible long enough to register but not so
        // long that a second copy attempt looks like it didn't take.
        window.setTimeout(() => setCopied(false), 1600);
      } catch {
        // clipboard denied — silent; the row remains usable via the link
      }
    },
    [adapter.contractAddress],
  );

  return (
    <AdapterPanelShell
      adapter={{ ...adapter, label: adapter.label ?? adapter.contractName }}
      belowStats={belowStats}
    >
      {adapter.headlineMetric && (
        <div className="text-[11px] text-amber-200/90 mb-1.5 truncate">
          {adapter.headlineMetric}
        </div>
      )}
      <div className="flex items-center gap-1.5 text-[9px] font-mono tracking-[0.12em] uppercase">
        <span className="text-zinc-500 shrink-0">Contract</span>
        {contractHref ? (
          <a
            href={contractHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={guardClick(contractHref)}
            className={cn(
              'inline-flex items-center gap-1 min-w-0 text-[10.5px] tabular-nums',
              'text-zinc-100 hover:text-amber-100 hover:underline underline-offset-2 decoration-amber-400/60',
            )}
          >
            <span className="truncate">{truncateAddress(adapter.contractAddress)}</span>
            <ExternalLink aria-hidden className="h-2.5 w-2.5 shrink-0 opacity-60" />
          </a>
        ) : (
          <span className="text-[10.5px] tabular-nums truncate text-zinc-100">
            {adapter.contractAddress
              ? truncateAddress(adapter.contractAddress)
              : '—'}
          </span>
        )}
        {adapter.contractAddress ? (
          <button
            type="button"
            onClick={onCopy}
            aria-label={copied ? 'Copied' : 'Copy contract address'}
            title={copied ? 'Copied' : 'Copy contract address'}
            className={cn(
              'shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-[2px]',
              'border border-white/10 bg-black/40 text-zinc-300',
              'hover:text-amber-100 hover:border-amber-400/40 transition-colors',
              copied && 'text-emerald-300 border-emerald-400/40',
            )}
          >
            {copied ? (
              <Check aria-hidden className="h-2.5 w-2.5" />
            ) : (
              <Copy aria-hidden className="h-2.5 w-2.5" />
            )}
          </button>
        ) : null}
      </div>
    </AdapterPanelShell>
  );
}
